import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  loading?: boolean;
  quickReplies?: string[];
}

interface ParsedChatAnswer {
  text: string;
  quickReplies?: string[];
}

type RobotChatTestModalData = {
  serviceNumberId: string;
  robotId?: string | null;
  robotName?: string | null;
};

const createChatIdentity = (prefix: 'user' | 'session'): string => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const getTextValue = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const getQuickReplyLabel = (item: unknown): string => {
  if (!isRecord(item)) {
    return '';
  }

  const action = isRecord(item['action']) ? item['action'] : {};
  return (
    getTextValue(action['displayText']) ||
    getTextValue(item['displayText']) ||
    getTextValue(action['title']) ||
    getTextValue(item['title']) ||
    getTextValue(action['data']) ||
    getTextValue(item['text'])
  );
};

const parseAnswerNode = (node: unknown): ParsedChatAnswer | null => {
  if (!isRecord(node)) {
    return null;
  }

  const text = getTextValue(node['text']) || getTextValue(node['message']) || getTextValue(node['answer']);
  const quickReply = node['quickReply'];
  const quickReplies =
    isRecord(quickReply) && Array.isArray(quickReply['items'])
      ? quickReply['items'].map(getQuickReplyLabel).filter((label): label is string => Boolean(label))
      : [];

  return text || quickReplies.length ? { text, quickReplies: quickReplies.length ? quickReplies : undefined } : null;
};

const parseChatAnswer = (answer: unknown, fallbackText: string): ParsedChatAnswer => {
  const rawText = getTextValue(answer);
  if (!rawText) {
    return { text: fallbackText };
  }

  try {
    const parsed = JSON.parse(rawText) as unknown;
    const normalized = typeof parsed === 'string' ? (JSON.parse(parsed) as unknown) : parsed;
    const answers = (Array.isArray(normalized) ? normalized : [normalized])
      .map(parseAnswerNode)
      .filter((item): item is ParsedChatAnswer => item !== null);

    if (!answers.length) {
      return { text: rawText };
    }

    const text = answers
      .map(item => item.text)
      .filter(Boolean)
      .join('\n\n');
    const quickReplies = Array.from(new Set(answers.flatMap(item => item.quickReplies ?? [])));
    return {
      text: text || fallbackText,
      quickReplies: quickReplies.length ? quickReplies : undefined
    };
  } catch {
    return { text: rawText };
  }
};

@Component({
  selector: 'robot-chat-test',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzIconModule, NzInputModule, NzSpinModule, I18nPipe, TestIdDirective],
  templateUrl: './robot-chat-test.component.html',
  styleUrls: ['./robot-chat-test.component.less']
})
export class RobotChatTestComponent implements OnInit, AfterViewChecked {
  private readonly modalRef = inject(NzModalRef);
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  readonly modalData: RobotChatTestModalData = inject(NZ_MODAL_DATA, { optional: true }) || { serviceNumberId: '' };

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef<HTMLTextAreaElement>;

  messages: ChatMessage[] = [];
  inputMessage = '';
  sending = false;
  private readonly userId = createChatIdentity('user');
  private readonly sessionId = createChatIdentity('session');
  private shouldScrollToBottom = false;

  get latestQuickReplies(): string[] {
    const latestMessage = this.messages[this.messages.length - 1];
    return latestMessage?.role === 'assistant' && !latestMessage.loading ? (latestMessage.quickReplies ?? []) : [];
  }

  ngOnInit(): void {
    this.initConversation();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private initConversation(): void {
    // 添加欢迎消息
    this.addMessage('assistant', this.i18n.fanyi('robot-chat.welcome-message'));
  }

  sendMessage(): void {
    const message = this.inputMessage.trim();
    if (!message || this.sending) {
      return;
    }

    // 添加用户消息
    this.addMessage('user', message);
    this.inputMessage = '';

    // 添加加载中的助手消息
    const loadingMessageId = this.addMessage('assistant', '', true);
    this.sending = true;

    // 调用后端接口
    const body = {
      serviceNumberId: this.modalData.serviceNumberId,
      robotId: this.modalData.robotId ?? null,
      message,
      userId: this.userId,
      sessionId: this.sessionId,
      chatMode: 'knowledge_base',
      channel: '測試對話'
    };

    this.http
      .post(ConstantUtil.ADMIN_SERVICENUMBER_ROBOT_CHAT_TEST, body)
      .pipe(
        finalize(() => {
          this.sending = false;
          setTimeout(() => this.messageInput?.nativeElement.focus());
        })
      )
      .subscribe({
        next: res => {
          if (res?.success && res?.data) {
            // 更新加载消息为实际回复
            const answer = parseChatAnswer(res.data.reply, this.i18n.fanyi('robot-chat.no-response'));
            this.updateMessage(loadingMessageId, answer.text, answer.quickReplies);
          } else {
            this.updateMessage(loadingMessageId, res?.msg || this.i18n.fanyi('robot-chat.error-response'));
          }
        },
        error: err => {
          this.updateMessage(loadingMessageId, this.i18n.fanyi('robot-chat.error-response'));
          this.message.error(this.i18n.fanyi('robot-chat.send-failed'));
        }
      });
  }

  private addMessage(role: 'user' | 'assistant', content: string, loading = false): string {
    const id = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    this.messages.push({
      id,
      role,
      content,
      timestamp: new Date(),
      loading
    });
    this.shouldScrollToBottom = true;
    return id;
  }

  private updateMessage(id: string, content: string, quickReplies?: string[]): void {
    const message = this.messages.find(m => m.id === id);
    if (message) {
      message.content = content;
      message.quickReplies = quickReplies;
      message.loading = false;
    }
  }

  sendQuickReply(reply: string): void {
    if (this.sending) {
      return;
    }

    this.inputMessage = reply;
    this.sendMessage();
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    } catch (err) {}
  }

  clearChat(): void {
    this.messages = [];
    this.addMessage('assistant', this.i18n.fanyi('robot-chat.welcome-message'));
  }

  close(): void {
    this.modalRef.close();
  }

  handleKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }
}
